import {
  Flag,
  CalendarIcon,
  countryName,
  Dialog,
  formatMoney,
  formatWhole,
  getCountryFacts,
  getFamily,
  getPlan,
  modes,
  toIso,
  NotFound,
  dayKinds,
  getLeave,
  markDay,
  type Booking,
  type CountriesFacts,
  type DayKind,
  type Family,
  type Kin,
  type LeavePerson,
  type Plan,
  type PlanStop,
  type Traveller,
  live,
} from '@trippath/shared';
import { format } from 'date-fns';
import { enGB, et } from 'date-fns/locale';
import { Fragment, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { HOME, monthCells, tripDays, tripMonths, weekdayOf, type TripDay } from './calendar';
import { day, priceSaid } from '../../lib/format';
import { iataOf } from '../../lib/airports';
import { lasting, wallOf } from '../../lib/clock';
import { ModeIcon } from '../../components/icons/ModeIcon';
import { gapsOf, unplanned } from '../../lib/gaps';
import { WayDetail, WayWait, wayTimes } from '../../components/way/WayDetail';
import { ChangeBooking, EditStop } from '../../components/stop/StopEdits';
import { DayKindIcon } from '../../components/icons/DayKindIcon';
import { kindOn, works } from './leave';
import {
  animalOf,
  calendarOf,
  dateIn,
  lunarOf,
  monthAround,
  monthName as localMonthName,
  monthsAcross,
  weekendOf,
  weekStartIn,
  type LocalCalendar,
} from '../../lib/localCalendar';
import { plusDays } from '../../lib/countryFacts';
import { LocalMonth } from './LocalMonth';
import { EclipseIcon } from '../../components/icons/EclipseIcon';
import { MoonIcon } from '../../components/icons/MoonIcon';
import { DayOffIcon, HolidayIcon } from '../../components/icons/DayOffIcon';
import { skyDay } from '../../lib/sky';
import { eclipsesSaid, type EclipseSaid, type EclipseSky } from './eclipseSaid';
import { moonOf } from './skySaid';
import { SunLine } from './SunLine';
import type { Sector } from '../../components/booking/TicketEditor';
import { sectorOf, ticketOf } from '../../lib/wayTicket';
import { CalendarMenu } from './CalendarMenu';
import { LeaveDialog } from './LeaveDialog';
import { ReadOnlySet, useReadOnly } from '../../app/readOnly';
import { joinsAt } from '../../components/travellers/Travellers';
import { branchesOf, meetsCity, parentsOf } from '../../components/family/Family';
import { byTime, kinDays, type KinDay } from './kinCalendar';
import { useLive } from '../../app/live';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

const locales: Record<string, string> = { et: 'et-EE' };

/** A day of another trip of the tree, as a day of the calendar shows it under its own. */
type KinOn = KinDay & { trip: Kin };
const NO_KIN: readonly KinOn[] = [];

/** Between two ways of a day, the change from one to the next: where, and how long to wait. */
function ChangeBetween({ stop, next }: { stop: PlanStop; next: PlanStop | undefined }) {
  if (!next) return null;
  const arrivalAt = wayTimes(stop).arrivalAt;
  const departureAt = wayTimes(next).departureAt;
  if (arrivalAt === null || departureAt === null) return null;
  return (
    <div className="leg-change">
      <WayWait city={next.city} wait={lasting(arrivalAt, departureAt)} />
    </div>
  );
}

/** The trip as months: where each night is spent, the days of travel, what is booked. */
/** The clock a day off the trip is read by: the one here. */
const HERE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** An eclipse's mark beside a day's number, said in full when pointed at. */
function EclipseMarks({ eclipses }: { eclipses: readonly EclipseSaid[] }) {
  return eclipses.map((eclipse) => (
    <span key={eclipse.name} className="day-eclipse" title={eclipseText(eclipse)}>
      <EclipseIcon body={eclipse.body} />
    </span>
  ));
}

const eclipseText = (eclipse: EclipseSaid) =>
  eclipse.seen === '' ? eclipse.name : `${eclipse.name} · ${eclipse.seen}`;

export function Calendar() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const locale = locales[lang] ?? 'en-GB';
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  // A day asked for by its link: another trip's calendar opened at the day pressed there.
  const [search] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // The trip's branches, each a line of its days under the trip's own; hidden from its menu.
  const [family, setFamily] = useState<Family | null>(null);
  // The days the trip's countries keep, as the Countries page reads them.
  const [abroad, setAbroad] = useState<CountriesFacts | null>(null);
  // The trips whose showing was changed from the menu: a branch hidden, a parent shown.
  const [flipped, setFlipped] = useState<string[]>([]);
  // The month shown: the day picked's until another is asked for.
  const [asked, setMonth] = useState<number | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  // Each person's week and marked days, and whose is open in its dialog.
  const [people, setPeople] = useState<LeavePerson[]>([]);
  const [leaveOf, setLeaveOf] = useState<string | null>(null);
  const locked = useReadOnly();
  // The calendar the month is laid out by: ours until another is picked.
  const [layout, setLayout] = useState('gregory');
  // The day of the month another calendar shows, once one is asked for there.
  const [localAt, setLocalAt] = useState<string | null>(null);
  // The eclipses, by a library of their own fetched once the calendar is open.
  const [eclipses, setEclipses] = useState<EclipseSky | null>(null);
  useEffect(() => {
    void import('../../lib/eclipses').then(setEclipses);
  }, []);
  // A way or its ticket being changed in a dialog.
  const [editing, setEditing] = useState<
    | { kind: 'stop'; stop: PlanStop }
    | { kind: 'way'; stop: PlanStop }
    | { kind: 'booking'; stop: PlanStop; booking: Booking; sector: Sector | null }
    | null
  >(null);

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    // Without them the calendar stands as it is.
    getLeave(id).then(setPeople, () => undefined);
    getFamily(id).then(setFamily, () => undefined);
  }, [id, changed]);
  useEffect(() => {
    // Without them only home's holidays are said.
    getCountryFacts(id, lang).then(setAbroad, () => undefined);
  }, [id, changed, lang]);
  // The trips it parts from, above it, shown when asked for; its branches shown unless unticked.
  const others = useMemo(
    () =>
      family
        ? [
            ...parentsOf(family, id).map((trip) => ({ trip, days: kinDays(trip), up: true })),
            ...branchesOf(family, id).map((trip) => ({ trip, days: kinDays(trip), up: false })),
          ]
        : [],
    [family, id],
  );
  const kinMenu = useMemo(
    () =>
      others.map(({ trip, up }) => ({
        id: trip.id,
        name: trip.name,
        place: trip.place,
        seen: up === flipped.includes(trip.id),
      })),
    [others, flipped],
  );
  const onward = family ? meetsCity(family, id) : null;

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { plan } = state;
  const leaveFor = people.find((person) => person.person === leaveOf);
  // The calendars the trip's countries keep, each once, with the first country keeping it.
  const systems: { own: LocalCalendar; country: string }[] = [];
  for (const stop of plan.stops) {
    const own = calendarOf(stop.country);
    if (own && !systems.some((one) => one.own.system === own.system)) {
      systems.push({ own, country: stop.country });
    }
  }
  const laidOut = systems.find((one) => one.own.system === layout);
  const stopEditing = () => {
    setEditing(null);
  };
  // What a dialog keeps is the plan from now on; the day picked stays.
  const saved = (next: Plan) => {
    setEditing(null);
    setState({ kind: 'ready', plan: next });
  };
  const { first, days } = tripDays(plan, abroad?.countries);
  const kinShown = others.filter(({ trip, up }) => up === flipped.includes(trip.id));
  const kinOn = (iso: string): readonly KinOn[] => {
    const on = kinShown.flatMap(({ trip, days: its }) => {
      const kin = its.get(iso);
      return kin ? [{ ...kin, trip }] : [];
    });
    return on.length > 0 ? on.sort(byTime) : NO_KIN;
  };
  // The months go on while a branch shown does.
  const lastShown = kinShown.reduce(
    (last, { trip }) => (trip.end > last ? trip.end : last),
    plan.trip.end,
  );
  // Days no stop covers, before a way on leaves later.
  const gaps = gapsOf(plan);
  const firstShown = kinShown.reduce(
    (soonest, { trip }) => (trip.start < soonest ? trip.start : soonest),
    plan.trip.start,
  );
  const months = tripMonths(plan, lastShown, firstShown);
  // Each country its tint, the trip's first, then those only its branches go to.
  const countries = [
    ...new Set([
      ...plan.countries.map((c) => c.country),
      ...kinShown.flatMap(({ trip }) => trip.stops.map((one) => one.country)),
    ]),
  ];
  const tints = new Map(countries.map((country, i) => [country, i % 6]));
  const today = toIso(new Date());
  // By its day, so a plan changed in a dialog says the day as it is now; today while the trip
  // runs, else its first.
  const pickedDay = picked ?? search.get('day') ?? today;
  const selected = days.get(pickedDay) ?? first;
  // A day after the trip a branch still goes on: its branches beside the calendar.
  const kinDay = !days.has(pickedDay) && kinOn(pickedDay).length > 0 ? pickedDay : null;
  const month =
    asked ?? months.findIndex(([year, index]) => inMonth(kinDay ?? selected.iso, year, index));
  // The day another calendar opens at: the one picked while in the month shown, else the month's
  // first — the month looked at stays in view.
  const shownDay = months
    .flatMap(([year, index], at) =>
      at === month
        ? [
            inMonth(selected.iso, year, index)
              ? selected.iso
              : `${String(year)}-${String(index + 1).padStart(2, '0')}-01`,
          ]
        : [],
    )
    .join('');
  const localShown = localAt ?? shownDay;
  const booked = selected.bookings.filter(({ booking }) => booking.kind !== 'ticket');
  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const longDay = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
      Date.UTC(2026, 0, 5 + i),
    ),
  );
  const how = (mode: string | undefined) => (mode ? t(`mode.${mode}`) : t('calendar.travel'));
  const shown = (traveller: Traveller) => !hidden.includes(traveller.id);
  const theirs = selected.theirs.filter((x) => shown(x.traveller));
  const together = selected.together.filter(shown);
  const selectedStop = selected.stay?.stop;
  const selectedSky =
    selectedStop?.place && selectedStop.zone
      ? skyDay(selected.iso, selectedStop.place, selectedStop.zone)
      : null;
  // Its moon said shortly, a set on a later day raised beside it.
  const selectedMoon = selectedSky ? moonOf(selectedSky, selected.iso, t, lang) : null;

  // A day's eclipses as its stop sees them; a day off the trip's by the clock here.
  const eclipsesAt = (iso: string, stop: PlanStop | undefined) =>
    eclipses === null
      ? []
      : eclipsesSaid(
          eclipses,
          iso,
          stop
            ? { city: stop.city, place: stop.place, zone: stop.zone ?? HERE }
            : { city: '', place: null, zone: HERE },
          t,
          lang,
        );
  const selectedEclipses = eclipsesAt(selected.iso, selectedStop);

  // The branches on a day beside the calendar: each in its colour, where it is, its own calendar
  // a link away.
  const kinBox = (iso: string) =>
    kinOn(iso).length > 0 && (
      <ul className="day-kin-list" aria-label={t('calendar.kinTitle')}>
        {kinOn(iso).map((on) => (
          <li key={on.trip.id} className={`kin-${String(on.trip.place % 6)}`}>
            {/* Whose it is by its colour, its name said on hover and to a screen reader. */}
            <span
              className="kin-dot"
              role="img"
              aria-label={on.trip.name}
              title={on.trip.name}
            />
            <span className="kin-line">
              <KinLine on={on} wide />
            </span>
            <Link
              className="icon-button icon-button-small"
              to={`/trips/${on.trip.id}/calendar?day=${iso}`}
              aria-label={t('calendar.kinOpen', { name: on.trip.name, day: day(iso, lang) })}
              title={t('calendar.kinOpen', { name: on.trip.name, day: day(iso, lang) })}
            >
              <CalendarIcon />
            </Link>
          </li>
        ))}
      </ul>
    );

  // A day of the calendar, by its number in the calendar shown — with ours under it in another —
  // its weekend there shaded: the trip's days all they say, the others only their number.
  const cellOf = (
    cell: string,
    number: number,
    ours?: string,
    off = weekendOf(HOME).includes(weekdayOf(cell)),
  ) => {
    const tripDay = days.get(cell);
    const kin = kinOn(cell);
    if (!tripDay) {
      const numbered = (
        <span className={cell === today ? 'day-number day-today' : 'day-number'}>{number}</span>
      );
      if (kin.length === 0) {
        return (
          <span
            key={cell}
            className={`day day-outside${off ? ' day-off' : ''}`}
            aria-hidden="true"
          >
            <span className="day-top">
              {numbered}
              <span className="day-signs">
                <EclipseMarks eclipses={eclipsesAt(cell, undefined)} />
              </span>
            </span>
            {ours && <span className="day-ours">{ours}</span>}
          </span>
        );
      }
      // Not this trip's, a branch's still: picked, the day is said beside the calendar.
      return (
        <button
          key={cell}
          type="button"
          className={`day day-outside${off ? ' day-off' : ''}`}
          aria-pressed={kinDay === cell}
          aria-label={[
            day(cell, lang),
            ...kin.map((on) => t('calendar.kinDay', { name: on.trip.name, city: on.city })),
          ].join(', ')}
          onClick={() => {
            setPicked(cell);
          }}
        >
          <span className="day-top">
            {numbered}
            {ours && <span className="day-ours">{ours}</span>}
          </span>
          {kin.map((on) => (
            <KinCell key={on.trip.id} on={on} />
          ))}
        </button>
      );
    }
    return (
      <DayCell
        key={cell}
        tripDay={tripDay}
        number={number}
        ours={ours}
        off={off}
        tint={tripDay.stay ? tints.get(tripDay.stay.stop.country) : undefined}
        selected={kinDay === null && selected.iso === cell}
        today={cell === today}
        marks={people.flatMap((person) => {
          const kind = kindOn(person, cell);
          return kind ? [{ name: person.name, kind }] : [];
        })}
        open={unplanned(gaps, cell)}
        eclipses={eclipsesAt(cell, tripDay.stay?.stop)}
        kin={kin}
        onward={onward}
        label={day(cell, lang)}
        how={how}
        shown={shown}
        onPick={() => {
          setPicked(tripDay.iso);
        }}
      />
    );
  };

  // What the days show, under the month whichever calendar lays it out.
  // Only what the trip's days show: a mark no day has is not explained.
  const ownDays = [...days.values()];
  const marked = new Set(
    ownDays.flatMap((one) => people.flatMap((person) => kindOn(person, one.iso) ?? [])),
  );
  const unbedded = ownDays.some(
    ({ stay, iso }) => stay !== null && !stay.stop.unresolved && !covered(stay.stop, iso),
  );
  const legend = (
    <ul className="calendar-legend" aria-label={t('calendar.legend')}>
      {dayKinds
        .filter((kind) => marked.has(kind))
        .map((kind) => (
          <li key={kind}>
            <span className={`day-mark mark-${kind}`} aria-hidden="true">
              <DayKindIcon kind={kind} size={12} />
            </span>
            {t(`leave.kind.${kind}`)}
          </li>
        ))}
      {ownDays.some((one) => holidaysOf(one, t).length > 0) && (
        <li>
          <span className="key-holiday" aria-hidden="true">
            <HolidayIcon />
          </span>
          {t('calendar.keyHoliday')}
        </li>
      )}
      <li>
        <span className="key-off" aria-hidden="true">
          <DayOffIcon />
        </span>
        {t('calendar.keyOff')}
      </li>
      {plan.travellers.length > 0 && (
        <li>
          <span className="key-leave" aria-hidden="true" />
          {t('calendar.keyTogether')}
        </li>
      )}
      {unbedded && (
        <li>
          <span className="key-gap" aria-hidden="true">
            !
          </span>
          {t('calendar.keyGap')}
        </li>
      )}
      {plan.stops.some((stop) => stop.unresolved) && (
        <li>
          <span className="key-unresolved" aria-hidden="true" />
          {t('calendar.keyUnresolved')}
        </li>
      )}
      {gaps.size > 0 && (
        <li>
          <span className="key-unplanned" aria-hidden="true" />
          {t('calendar.keyUnplanned')}
        </li>
      )}
      {ownDays.some((one) => eclipsesAt(one.iso, one.stay?.stop).length > 0) && (
        <li>
          <EclipseIcon body="sun" size={12} />
          {t('calendar.keyEclipse')}
        </li>
      )}
      {kinShown.map(({ trip }) => (
        <li key={trip.id} className={`kin-${String(trip.place % 6)}`}>
          <span className="kin-dot" aria-hidden="true" />
          {trip.name}
        </li>
      ))}
    </ul>
  );

  // The ways and money of the days within a span, as a month's head says them: each day's
  // stay, and each way on the day it goes.
  const summaryOf = (inside: (iso: string) => boolean) => {
    const within = [...days.values()].filter((d) => inside(d.iso));
    return t('calendar.summary', {
      count: within.reduce((sum, d) => sum + d.legs.length, 0),
      amount: formatWhole(
        {
          currency: plan.trip.currency,
          minor: within.reduce(
            (sum, d) =>
              sum +
              (d.stay?.stop.expectedPerDay?.minor ?? 0) +
              d.legs.reduce((all, leg) => all + wayExpected(leg.stop), 0),
            0,
          ),
        },
        lang,
      ),
    });
  };
  // The ways' colours and whose travel is shown, beside every month's name.
  const keys = (
    <div className="calendar-keys">
      <ul className="mode-keys" aria-label={t('map.legend')}>
        {/* Only the ways the trip and the other trips shown go by: no train without one. */}
        {modes
          .filter(
            (mode) =>
              plan.stops.some((stop) => stop.out?.mode === mode) ||
              kinShown.some(({ trip }) => trip.stops.some((stop) => stop.mode === mode)),
          )
          .map((mode) => (
            <li key={mode}>
              <span className={`mode-key mode-${mode}`} aria-hidden="true" />
              {t(`mode.${mode}`)}
            </li>
          ))}
      </ul>
      {plan.travellers.map((traveller) => (
        <label key={traveller.id} className="check">
          <input
            type="checkbox"
            checked={shown(traveller)}
            onChange={(event) => {
              setHidden((ids) =>
                event.target.checked
                  ? ids.filter((id) => id !== traveller.id)
                  : [...ids, traveller.id],
              );
            }}
          />
          {t('travellers.show', { name: traveller.name })}
        </label>
      ))}
    </div>
  );
  // The whole trip month by month, by the calendar the month is laid out by.
  const lastDay = [...days.keys(), ...(lastShown > plan.trip.end ? [lastShown] : [])].reduce(
    (last, at) => (at > last ? at : last),
    first.iso,
  );
  const strip = laidOut
    ? monthsAcross(firstShown, lastDay, laidOut.own.system).map(({ first: start, next }) => {
        const cells: string[] = [];
        for (let at = start; at < next; at = plusDays(at, 1)) cells.push(at);
        return {
          key: start,
          name: localMonthName(start, laidOut.own.system, locale),
          short: stripName(start, laidOut.own, shortMonth(Date.parse(start), locale)),
          pressed: monthAround(localShown, laidOut.own.system).first === start,
          cells,
          pick: () => {
            setLocalAt(start);
          },
        };
      })
    : months.map(([year, monthIndex], index) => ({
        key: `${String(year)}-${String(monthIndex)}`,
        name: monthName.format(Date.UTC(year, monthIndex, 1)),
        short: shortMonth(Date.UTC(year, monthIndex, 1), locale),
        pressed: index === month,
        cells: monthCells(year, monthIndex).filter((cell) => cell !== null),
        pick: () => {
          setMonth(index);
        },
      }));

  return (
    <div className="calendar-page">
      <h1 className="visually-hidden">{t('calendar.title')}</h1>
      <div className="calendar-head">
        {/* Ours, or a calendar the trip's countries keep: the month as it lays it out. */}
        {systems.length > 0 && (
          <div className="calendar-systems" role="group" aria-label={t('local.view')}>
            {[
              { system: 'gregory', label: t('local.system.gregory') },
              // A lunar calendar by the country keeping it: Vietnam's and Korea's differ.
              ...systems.map(({ own, country }) => ({
                system: own.system,
                label: own.lunar
                  ? `${t(`local.system.${own.system}`)} · ${countryName(country, lang)}`
                  : t(`local.system.${own.system}`),
              })),
            ].map(({ system, label }) => (
              <button
                key={system}
                type="button"
                className="chip"
                aria-pressed={system === layout}
                onClick={() => {
                  setLayout(system);
                  setLocalAt(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <CalendarMenu
          trip={plan.trip.id}
          kin={kinMenu}
          onFlip={(trip) => {
            setFlipped((ids) =>
              ids.includes(trip) ? ids.filter((one) => one !== trip) : [...ids, trip],
            );
          }}
        />
      </div>
      <div className="calendar-layout">
        <div className="months">
          {laidOut && (
            <LocalMonth
              key={laidOut.own.system}
              own={laidOut.own}
              weekend={weekendOf(laidOut.country)}
              weekStart={weekStartIn(laidOut.own, laidOut.country)}
              shown={localShown}
              onShow={setLocalAt}
              trip={{ first: firstShown, last: lastDay }}
              summary={(from, next) => summaryOf((iso) => iso >= from && iso < next)}
              keys={keys}
              cell={cellOf}
              legend={legend}
            />
          )}
          {!laidOut &&
            months.map(([year, monthIndex], index) => (
              <section
                key={`${String(year)}-${String(monthIndex)}`}
                className="panel calendar-card"
                aria-labelledby={`month-${String(index)}`}
                hidden={index !== month}
              >
                <div className="calendar-card-head">
                  <div className="month-head">
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={t('calendar.previous')}
                      disabled={index === 0}
                      onClick={() => {
                        setMonth(index - 1);
                      }}
                    >
                      ‹
                    </button>
                    <h2 id={`month-${String(index)}`}>
                      {monthName.format(Date.UTC(year, monthIndex, 1))}
                    </h2>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={t('calendar.next')}
                      disabled={index === months.length - 1}
                      onClick={() => {
                        setMonth(index + 1);
                      }}
                    >
                      ›
                    </button>
                    <span className="quiet month-summary">
                      {summaryOf((iso) => inMonth(iso, year, monthIndex))}
                    </span>
                  </div>
                  {keys}
                </div>
                <div className="month-grid">
                  {weekdays.map((name) => (
                    <span key={name} className="weekday" aria-hidden="true">
                      {name}
                    </span>
                  ))}
                  {monthCells(year, monthIndex).map((cell, at) =>
                    cell === null ? (
                      <span key={`lead-${String(at)}`} />
                    ) : (
                      cellOf(cell, Number(cell.slice(8)))
                    ),
                  )}
                </div>
                {legend}
              </section>
            ))}
        </div>

        <div className="calendar-side">
          {kinDay ? (
            // A day the trip's branches go on after it: where each is.
            <section
              className="panel day-details"
              aria-labelledby="day-title"
              aria-live="polite"
            >
              <p className="day-kicker">{t('calendar.afterTrip')}</p>
              <h2 id="day-title" className="day-title">
                {longDay.format(Date.parse(`${kinDay}T00:00:00Z`))}
              </h2>
              {kinBox(kinDay)}
            </section>
          ) : (
            <section
              className="panel day-details"
              aria-labelledby="day-title"
              aria-live="polite"
            >
              <p className="day-kicker">
                {selected.iso === today && (
                  <span className="day-kicker-today">{t('calendar.today')}</span>
                )}
                {t('calendar.tripDay', { day: selected.tripDay, days: days.size })}
              </p>
              <h2 id="day-title" className="day-title">
                {laidOut
                  ? dayIn(selected.iso, laidOut.own, locale, t)
                  : longDay.format(Date.parse(`${selected.iso}T00:00:00Z`))}
              </h2>
              {/* By another calendar: ours under it. */}
              {laidOut && (
                <p className="quiet day-ours-date">
                  {longDay.format(Date.parse(`${selected.iso}T00:00:00Z`))}
                </p>
              )}
              <p className="quiet">
                {selected.stay
                  ? t('calendar.night', {
                      city: selected.stay.stop.city,
                      country: countryName(selected.stay.stop.country, lang),
                      night: selected.stay.night,
                      nights: selected.stay.nights,
                    })
                  : t('calendar.lastDay')}
              </p>
              {/* The country's own calendar, when the month is not laid out by one. */}
              {selected.stay && !laidOut && (
                <LocalDate country={selected.stay.stop.country} day={selected.iso} />
              )}
              {/* A way landing this day that left the day before: when and where it lands, then
                the wait before the day's first way on. */}
              {selected.arrivals.map((arrival) => (
                <Fragment key={`in-${arrival.stop.id}`}>
                  <p className="leg-arrives">
                    <ModeIcon mode="arrival" size={16} />
                    {t('calendar.arrives', { time: arrival.time, place: arrival.place })}
                  </p>
                  <ChangeBetween stop={arrival.stop} next={selected.legs[0]?.stop} />
                </Fragment>
              ))}
              {selected.legs.map((selectedLeg, index) => (
                <Fragment key={selectedLeg.stop.id}>
                  <div className={`leg-box mode-top-${selectedLeg.stop.out?.mode ?? 'travel'}`}>
                    {selectedLeg.stop.out ? (
                      // All that is known of it: when, how long, its flights; the waits are between.
                      <WayDetail plan={plan} stop={selectedLeg.stop} />
                    ) : (
                      <p className="leg-box-head">
                        <strong>
                          {selectedLeg.next
                            ? t('calendar.leaving', {
                                how: how(undefined),
                                city: selectedLeg.next.city,
                              })
                            : t('calendar.leavingLast', { how: how(undefined) })}
                        </strong>
                      </p>
                    )}
                    {/* Changed here, in a dialog, by the icons at its top right: the way, and
                      its ticket — at a change its journey's. */}
                    {!locked && (
                      <WayActions
                        plan={plan}
                        stop={selectedLeg.stop}
                        onWay={() => {
                          setEditing({ kind: 'way', stop: selectedLeg.stop });
                        }}
                        onTicket={(stop, booking) => {
                          setEditing({
                            kind: 'booking',
                            stop,
                            booking,
                            sector: sectorOf(selectedLeg.stop),
                          });
                        }}
                      />
                    )}
                  </div>
                  <ChangeBetween
                    stop={selectedLeg.stop}
                    next={selected.legs[index + 1]?.stop}
                  />
                </Fragment>
              ))}
              {theirs.map(({ traveller, leg }) => (
                <p key={leg.id} className="their-leg">
                  <strong>{traveller.name}</strong> · {leg.way}
                  {leg.price ? ` · ${formatMoney(leg.price, lang)}` : ''}
                  {leg.reference ? ` · ${leg.reference}` : ''}
                </p>
              ))}
              {together.map((traveller) => (
                <p key={traveller.id} className="quiet">
                  {t('travellers.together', { name: traveller.name })}
                </p>
              ))}
              {/* The sun that day at the stop, and the moon that night, as the day's corner shows
                  it. */}
              {selectedSky && <SunLine sky={selectedSky} />}
              {selectedSky?.visible === true && (
                <p className="day-moon-said">
                  <MoonIcon phase={selectedSky.phase} turn={selectedSky.turn} size={18} />
                  <span>
                    {selectedMoon?.said}
                    {selectedMoon?.later && (
                      <sup className="day-later">{selectedMoon.later}</sup>
                    )}
                  </span>
                </p>
              )}
              {/* An eclipse that day, and how much of it the stop sees. */}
              {selectedEclipses.map((eclipse) => (
                <p key={eclipse.name} className="day-eclipse-said">
                  <EclipseIcon body={eclipse.body} size={18} />
                  <span>
                    <strong>{eclipse.name}</strong>
                    {eclipse.seen !== '' && ` · ${eclipse.seen}`}
                  </span>
                </p>
              ))}
              {(selected.holiday !== null || selected.local.length > 0) && (
                <ul className="holiday-box">
                  {selected.holiday !== null && (
                    <li>
                      {t('calendar.holidayIn', {
                        name: t(`holiday.${selected.holiday}`),
                        kind: t('calendar.publicHoliday'),
                        country: countryName(HOME, lang),
                      })}
                    </li>
                  )}
                  {selected.local.map((holiday) => (
                    <li key={holiday.name} className={holiday.public ? undefined : 'quiet'}>
                      {t('calendar.holidayIn', {
                        name: holiday.name,
                        kind: t(
                          holiday.public ? 'calendar.publicHoliday' : 'calendar.observance',
                        ),
                        country: countryName(selected.country, lang),
                      })}
                    </li>
                  ))}
                </ul>
              )}
              {/* A trip read only: who is away when said, nothing marked or opened to change. */}
              <ReadOnlySet>
                {people.length > 0 && (
                  <section className="day-people-box" aria-labelledby="day-people">
                    <h3 id="day-people">{t('leave.people')}</h3>
                    <ul className="day-people">
                      {people.map((person) => (
                        <PersonDay
                          key={person.person}
                          person={person}
                          day={selected.iso}
                          onMark={(kind) => {
                            markDay(plan.trip.id, person.person, selected.iso, kind).then(
                              setPeople,
                              () => undefined,
                            );
                          }}
                          onOpen={() => {
                            setLeaveOf(person.person);
                          }}
                        />
                      ))}
                    </ul>
                  </section>
                )}
                {/* Its tickets are with their ways above: the rest booked for the day here. */}
                {booked.length > 0 && (
                  <ul className="day-bookings" aria-label={t('calendar.booked')}>
                    {/* Each opened in its dialog: all that is said of it, to change. */}
                    {booked.map(({ booking, stop }) => (
                      <li key={booking.id}>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            setEditing({ kind: 'booking', stop, booking, sector: null });
                          }}
                        >
                          {booking.name}
                        </button>{' '}
                        · {priceSaid(booking.price, booking.paid, lang)}
                      </li>
                    ))}
                  </ul>
                )}
              </ReadOnlySet>
              {kinBox(selected.iso)}
              {selected.stay?.stop.expectedPerDay && (
                <p className="day-expected">
                  <span className="mono">
                    {formatWhole(selected.stay.stop.expectedPerDay, lang)}
                  </span>
                  <span className="quiet">{t('calendar.expectedThatDay')}</span>
                </p>
              )}
            </section>
          )}

          <section className="panel whole-trip" aria-labelledby="whole-title">
            <h2 id="whole-title">{t('calendar.whole')}</h2>
            {strip.map((row) => (
              <button
                key={row.key}
                type="button"
                className="strip-row"
                aria-pressed={row.pressed}
                aria-label={t('calendar.show', { month: row.name })}
                onClick={row.pick}
              >
                <span className="strip-month">{row.short}</span>
                {/* The trip's days by country; after it, a day its branches go on split
                    between them, each in its country's tint. */}
                <span className="strip-days" aria-hidden="true">
                  {row.cells.map((cell) => {
                    const stay = days.get(cell)?.stay;
                    const tint = stay ? tints.get(stay.stop.country) : undefined;
                    const kin = days.has(cell) ? NO_KIN : kinOn(cell);
                    if (kin.length > 0) {
                      return (
                        <span key={cell} className="strip-day strip-split">
                          {kin.map((on) => (
                            <span
                              key={on.trip.id}
                              className={`strip-${String(tints.get(on.country))}`}
                            />
                          ))}
                        </span>
                      );
                    }
                    return (
                      <span
                        key={cell}
                        className={
                          stay?.stop.unresolved
                            ? 'strip-day strip-unresolved'
                            : tint === undefined
                              ? 'strip-day'
                              : `strip-day strip-${String(tint)}`
                        }
                      />
                    );
                  })}
                </span>
              </button>
            ))}
            <ul className="strip-keys">
              {countries.map((country, index) => (
                <li key={country} title={countryName(country, lang)}>
                  <span className={`strip-day strip-${String(index % 6)}`} aria-hidden="true" />
                  <Flag country={country} />
                  {country}
                </li>
              ))}
            </ul>
          </section>

          {plan.travellers.map((traveller) => {
            const city = joinsAt(plan, traveller);
            const dates = t('trips.dates', {
              from: day(traveller.joins, lang),
              to: day(traveller.leaves, lang),
            });
            const calendarDays =
              Math.round(
                (Date.parse(`${traveller.leaves}T00:00:00Z`) -
                  Date.parse(`${traveller.joins}T00:00:00Z`)) /
                  86_400_000,
              ) + 1;
            return (
              <section
                key={traveller.id}
                className="panel leave-planner"
                aria-labelledby={`leave-${traveller.id}`}
              >
                <h2 id={`leave-${traveller.id}`}>
                  {t('travellers.planner', { name: traveller.name })}
                </h2>
                <p className="leave-head">
                  <span>
                    {city
                      ? t('travellers.stay', { city, dates })
                      : t('travellers.stayElsewhere', { dates })}
                  </span>
                  <strong>{t('travellers.leaveDays', { count: traveller.leaveDays })}</strong>
                </p>
                <p className="quiet">
                  {traveller.holidays.length > 0
                    ? t('travellers.holidays', {
                        count: traveller.holidays.length,
                        days: calendarDays,
                        leave: traveller.leaveDays,
                      })
                    : t('travellers.noHolidays', {
                        days: calendarDays,
                        leave: traveller.leaveDays,
                      })}
                </p>
              </section>
            );
          })}
        </div>
      </div>
      {leaveFor && (
        <LeaveDialog
          trip={plan.trip.id}
          person={leaveFor}
          onClose={() => {
            setLeaveOf(null);
          }}
          onSaved={(next) => {
            setLeaveOf(null);
            setPeople(next);
          }}
        />
      )}
      {/* A way pressed: all that is known of it, its ticket and its transport to change. */}
      {editing?.kind === 'way' && (
        <WayDialog
          plan={plan}
          stop={editing.stop}
          onClose={stopEditing}
          onTransport={() => {
            setEditing({ kind: 'stop', stop: editing.stop });
          }}
          onTicket={(stop, booking) => {
            setEditing({ kind: 'booking', stop, booking, sector: sectorOf(editing.stop) });
          }}
        />
      )}
      {editing?.kind === 'stop' && (
        <EditStop plan={plan} stop={editing.stop} onClose={stopEditing} onSaved={saved} />
      )}
      {editing?.kind === 'booking' && (
        <ChangeBooking
          plan={plan}
          stop={editing.stop}
          booking={editing.booking}
          sector={editing.sector}
          onClose={stopEditing}
          onSaved={saved}
        />
      )}
    </div>
  );
}

/**
 * A branch on a day: the day it arrives, the way it came as the trip's own ways are drawn —
 * struck through when cancelled — else the town its night is in.
 */
function KinLine({ on, wide = false }: { on: KinOn; wide?: boolean }) {
  if (!on.by) return <span className="day-kin-city">{on.city}</span>;
  return (
    <span className={on.gone ? 'day-leg day-leg-gone' : `day-leg mode-${on.by}`}>
      <ModeIcon mode={on.by} size={12} />
      {on.at && <span className="day-leg-time">{wallOf(on.at, on.zone).time}</span>}
      <span className="day-leg-where">
        {on.codes ?? (wide && on.from ? `${on.from} → ${on.city}` : on.city)}
      </span>
    </span>
  );
}

/** A branch in a day of the calendar: its dot in its colour, then where it is. */
function KinCell({ on }: { on: KinOn }) {
  return (
    <span className={`day-kin kin-${String(on.trip.place % 6)}`}>
      <span className="kin-dot" aria-hidden="true" />
      <KinLine on={on} />
    </span>
  );
}

/** Whether `iso` falls in the month. */
function inMonth(iso: string, year: number, monthIndex: number): boolean {
  return iso.startsWith(`${String(year)}-${String(monthIndex + 1).padStart(2, '0')}`);
}

/** Whether a booked stay covers the night of `iso` at its stop. */
function covered(stop: PlanStop, iso: string): boolean {
  return stop.bookings.some((booking) => {
    if (booking.kind !== 'stay' || booking.day === null) return false;
    const from = Date.parse(`${booking.day}T00:00:00Z`);
    const at = Date.parse(`${iso}T00:00:00Z`);
    return at >= from && at < from + booking.nights * 86_400_000;
  });
}

/**
 * The public holidays a day falls on, at home first, then where it is spent: home's in the UI's
 * language, the country's as its calendar names it.
 */
function holidaysOf(tripDay: TripDay, t: TFunction): string[] {
  return [
    ...(tripDay.holiday === null ? [] : [t(`holiday.${tripDay.holiday}`)]),
    ...tripDay.local.filter((holiday) => holiday.public).map((holiday) => holiday.name),
  ];
}

interface CellProps {
  tripDay: TripDay;
  number: number;
  tint: number | undefined;
  selected: boolean;
  today: boolean;
  /** Our date under the number, when the month is laid out by another calendar. */
  ours?: string | undefined;
  /** Its weekend in the calendar shown. */
  off?: boolean;
  /** What each person marked the day: on leave, sick, working. */
  marks: { name: string; kind: DayKind }[];
  /** No stop covers it: days to plan. */
  open: boolean;
  /** The eclipses that day, as its stop sees them. */
  eclipses: readonly EclipseSaid[];
  /** The trip's branches this day: where each is. */
  kin: readonly KinOn[];
  /** Where the trip joins another, its last way going on there. */
  onward: string | null;
  label: string;
  how: (mode: string | undefined) => string;
  shown: (traveller: Traveller) => boolean;
  onPick: () => void;
}

/**
 * A month as the strip has room for, short as its language writes it, the same in every browser:
 * `jaan.`, `märts`, `dets.`; `Jan`, `Sept`. Estonian marks a word cut short with a dot.
 */
function shortMonth(at: number, locale: string): string {
  const estonian = locale === 'et-EE';
  const dates = { locale: estonian ? et : enGB };
  // Mid-month on the clock here, whatever its zone.
  const month = new Date(new Date(at).getUTCFullYear(), new Date(at).getUTCMonth(), 15);
  const short = format(month, 'LLL', dates);
  return estonian && short !== format(month, 'LLLL', dates) ? `${short}.` : short;
}

/**
 * A day by another calendar, its weekday first: `reede, 19. džumaada I 1448 AH`; where months
 * follow the moon, by its lunar day and month.
 */
function dayIn(day: string, own: LocalCalendar, locale: string, t: TFunction): string {
  const at = Date.parse(`${day}T00:00:00Z`);
  if (!own.lunar) {
    return new Intl.DateTimeFormat(`${locale}-u-ca-${own.system}`, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(at);
  }
  const moon = lunarOf(day, own.system);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(
    at,
  );
  return `${weekday}, ${t('local.lunarDay', { day: moon.day, month: moon.month })}`;
}

/**
 * A month of another calendar as the strip has room for: ours where its months are ours, a lunar
 * one by its number, else its name in short: `Jum II`, `Kislev`.
 */
function stripName(day: string, own: LocalCalendar, ours: string): string {
  if (own.ourMonths) return ours;
  if (own.lunar) return `${String(lunarOf(day, own.system).month)}.`;
  return new Intl.DateTimeFormat(`en-u-ca-${own.system}`, { month: 'short', timeZone: 'UTC' })
    .format(Date.parse(`${day}T00:00:00Z`))
    .replace('.', '')
    .slice(0, 6);
}

/**
 * What a way on is expected to cost, in minor units, as the budget counts it: its tickets once
 * one is bought, else the middle of its estimate.
 */
function wayExpected(stop: PlanStop): number {
  const tickets = stop.bookings.filter((booking) => booking.kind === 'ticket' && live(booking));
  const out = stop.out;
  if (tickets.length > 0 || !out)
    return tickets.reduce((sum, ticket) => sum + ticket.price.minor, 0);
  return Math.floor((out.estimate.min.minor + out.estimate.max.minor) / 2);
}

/**
 * Where a leg goes, as its pill says it: `DAD→KUL` by air, else the town it reaches — from the
 * last stop, where the trip joins another — else nothing.
 */
function whereTo(
  leg: { stop: PlanStop; next: PlanStop | null },
  onward: string | null = null,
): string {
  const out = leg.stop.out;
  const from = out?.mode === 'flight' ? iataOf(out.from?.name) : null;
  const to = out?.mode === 'flight' ? iataOf(out.to?.name) : null;
  if (from && to) return `${from}→${to}`;
  return leg.next ? leg.next.city : (onward ?? '');
}

/**
 * The day picked in the calendar of the country it is spent in, when it keeps its own: in words,
 * a lunar day by its month and day, and as written there.
 */
function LocalDate({ country, day }: { country: string; day: string }) {
  const { t, i18n } = useTranslation();
  const own = calendarOf(country);
  if (!own) return null;
  const moon = lunarOf(day, own.system);
  return (
    <div className="local-date">
      <span className="local-date-head">
        <span className="quiet">{t('local.day')}</span>
        <span className="quiet">{t(`local.system.${own.system}`)}</span>
      </span>
      <strong>
        {own.lunar
          ? `${t('local.lunarDay', { day: moon.day, month: moon.month })} · ${t(
              'local.animalYear',
              {
                animal: t(`local.animal.${String(animalOf(day, own.system))}`),
              },
            )}`
          : dateIn(day, own.system, i18n.language === 'et' ? 'et-EE' : 'en-GB')}
      </strong>
      <span className="local-date-native" lang={own.locale}>
        {dateIn(day, own.system, own.locale)}
      </span>
    </div>
  );
}

/**
 * A person on the day picked: their initial and name, what the day is for them — marked, or by
 * their working week when they keep one — and the icons that mark it, again to clear it; the
 * last opens their working week and leave.
 */
function PersonDay({
  person,
  day,
  onMark,
  onOpen,
}: {
  person: LeavePerson;
  day: string;
  onMark: (kind: DayKind | null) => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const kind = kindOn(person, day);
  // A working week is theirs to say: without one the day is only what they marked.
  const status = kind
    ? t(`leave.kind.${kind}`)
    : person.workdays.includes('1')
      ? t(works(person.workdays, day) ? 'leave.workday' : 'leave.dayOff')
      : null;
  return (
    <li className="day-person-row">
      <span className="person-initial" aria-hidden="true">
        {person.name.slice(0, 1)}
      </span>
      <span className="day-person">
        <strong>{person.name}</strong>
        {status !== null && (
          <span className={kind ? `day-person-state mark-${kind}` : 'day-person-state quiet'}>
            {status}
          </span>
        )}
      </span>
      <span className="day-person-marks" role="group" aria-label={person.name}>
        {dayKinds.map((one) => {
          const said = t('leave.mark', { name: person.name, kind: t(`leave.kind.${one}`) });
          return (
            <button
              key={one}
              type="button"
              className={`icon-button icon-button-small mark-${one}`}
              aria-pressed={kind === one}
              aria-label={said}
              title={said}
              onClick={() => {
                onMark(kind === one ? null : one);
              }}
            >
              <DayKindIcon kind={one} size={16} />
            </button>
          );
        })}
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('leave.open', { name: person.name })}
          title={t('leave.open', { name: person.name })}
          onClick={onOpen}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* After Lucide's sliders-horizontal (ISC licence). */}
            <path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3M14 2v4M8 10v4M16 18v4" />
          </svg>
        </button>
      </span>
    </li>
  );
}

/**
 * A way's icons at its box's top right: a pencil that changes the way, and a ticket's that opens
 * the ticket it goes on — its own, or at a change its journey's.
 */
/**
 * A way of the day in a dialog: when, how long, how far, its price, its ticket's flights and
 * seats; its ticket and its transport changed from there.
 */
function WayDialog({
  plan,
  stop,
  onClose,
  onTransport,
  onTicket,
}: {
  plan: Plan;
  stop: PlanStop;
  onClose: () => void;
  onTransport: () => void;
  onTicket: (stop: PlanStop, booking: Booking) => void;
}) {
  const { t } = useTranslation();
  const next = plan.stops[plan.stops.indexOf(stop) + 1];
  const { holder, ticket } = ticketOf(plan, stop);
  return (
    <Dialog
      open
      wide
      title={t('way.widget', { from: stop.city, to: next?.city ?? '…' })}
      onClose={onClose}
    >
      <WayDetail plan={plan} stop={stop} />
      <div className="dialog-actions">
        {ticket && (
          <button
            type="button"
            className="button"
            onClick={() => {
              onTicket(holder, ticket);
            }}
          >
            {t('calendar.editTicket')}
          </button>
        )}
        <button type="button" className="button button-primary" onClick={onTransport}>
          {t('calendar.editTransport')}
        </button>
      </div>
    </Dialog>
  );
}

function WayActions({
  plan,
  stop,
  onWay,
  onTicket,
}: {
  plan: Plan;
  stop: PlanStop;
  onWay: () => void;
  onTicket: (stop: PlanStop, booking: Booking) => void;
}) {
  const { t } = useTranslation();
  const { holder, ticket } = ticketOf(plan, stop);
  return (
    <span className="leg-box-actions">
      {ticket && (
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('calendar.editTicket')}
          title={t('calendar.editTicket')}
          onClick={() => {
            onTicket(holder, ticket);
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* After Lucide's ticket (ISC licence). */}
            <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2ZM13 5v2M13 17v2M13 11v2" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={t('calendar.editWay')}
        title={t('calendar.editWay')}
        onClick={onWay}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {/* After Lucide's pencil (ISC licence). */}
          <path d="M21.17 6.81a1 1 0 0 0-3.98-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5zM15 5l4 4" />
        </svg>
      </button>
    </span>
  );
}

/** One day of the trip: its number, where the night is, the travel and bookings of the day. */
function DayCell({
  tripDay,
  number,
  tint,
  selected,
  today,
  ours,
  off = false,
  marks,
  open,
  eclipses,
  kin,
  onward,
  label,
  how,
  shown,
  onPick,
}: CellProps) {
  const { t, i18n } = useTranslation();
  const gapLabel = t('calendar.keyGap');
  const { stay, legs, arrivals } = tripDay;
  const theirs = tripDay.theirs.filter((x) => shown(x.traveller));
  const together = tripDay.together.some(shown);
  const first = stay !== null && (stay.night === 1 || number === 1);
  const own = stay ? calendarOf(stay.stop.country) : null;
  const moon = own?.lunar ? lunarOf(tripDay.iso, own.system) : null;
  const holidays = holidaysOf(tripDay, t);
  // The moon, when it can be seen from the stop that night: drawn as it is seen there, when it
  // rises and sets by the stop's clock.
  const sky =
    stay?.stop.place && stay.stop.zone
      ? skyDay(tripDay.iso, stay.stop.place, stay.stop.zone)
      : null;
  const seen = sky?.visible === true ? moonOf(sky, tripDay.iso, t, i18n.language) : null;
  const moonSaid = seen ? `${seen.said}${seen.later}` : null;
  return (
    <button
      type="button"
      className={`day${tint === undefined ? '' : ` tint-${String(tint)}`}${stay?.stop.unresolved ? ' day-unresolved' : ''}${together ? ' day-together' : ''}${open ? ' day-unplanned' : ''}${off || tripDay.off ? ' day-off' : ''}`}
      aria-pressed={selected}
      aria-label={[
        label,
        ...(today ? [t('calendar.today')] : []),
        ...marks.map(({ name, kind }) =>
          t('leave.mark', { name, kind: t(`leave.kind.${kind}`) }),
        ),
        ...holidays,
        ...(open ? [t('calendar.unplanned')] : []),
        ...arrivals.map((leg) => t('calendar.arrives', { time: leg.time, place: leg.place })),
        ...legs.map((leg) => {
          const to = leg.next ? leg.next.city : onward;
          return `${how(leg.stop.out?.mode)}${to ? ` → ${to}` : ''}`;
        }),
        ...theirs.map(({ traveller, leg }) => `${traveller.name}: ${leg.way}`),
        ...(moon ? [t('local.lunarDay', { day: moon.day, month: moon.month })] : []),
        ...eclipses.map(eclipseText),
        ...kin.map((on) => t('calendar.kinDay', { name: on.trip.name, city: on.city })),
      ].join(', ')}
      onClick={onPick}
    >
      <span className="day-top">
        {/* Today by its number in a ring of the accent, and said. */}
        <span className={today ? 'day-number day-today' : 'day-number'}>{number}</span>
        {/* Beside its number: a day off by its umbrella, and the moon when it can be seen
            that night; each said in full when pointed at. */}
        <span className="day-signs" aria-hidden="true">
          {(off || tripDay.off) && (
            <span className="day-off-mark" title={t('calendar.offSaid')}>
              <DayOffIcon />
            </span>
          )}
          {sky && moonSaid !== null && (
            <span className="day-moon" title={moonSaid}>
              <MoonIcon phase={sky.phase} turn={sky.turn} size={16} />
            </span>
          )}
          <EclipseMarks eclipses={eclipses} />
        </span>
        {ours && <span className="day-ours">{ours}</span>}
        {today && (
          <span className="day-today-word" aria-hidden="true">
            {t('calendar.today')}
          </span>
        )}
        {stay && !stay.stop.unresolved && !covered(stay.stop, tripDay.iso) && (
          <span className="day-gap" title={gapLabel}>
            !
          </span>
        )}
      </span>
      {stay && (
        <span className="day-city-row">
          {/* The town not said twice: the way there this day already names it. */}
          {!legs.some((leg) => whereTo(leg, onward) === stay.stop.city) && (
            <span className={first ? 'day-city day-city-first' : 'day-city'}>
              {stay.stop.city}
            </span>
          )}
          {/* Where months follow the moon: the lunar day and month, new and full moon marked. */}
          {moon && (
            <span className="day-lunar" aria-hidden="true">
              {moon.day === 1 && <span title={t('local.newMoon')}>●</span>}
              {moon.day === 15 && <span title={t('local.fullMoon')}>○</span>}
              {`${String(moon.day)}/${String(moon.month)}`}
            </span>
          )}
        </span>
      )}
      {holidays.length > 0 && (
        <span className="day-holiday" aria-hidden="true">
          <HolidayIcon />
          {holidays.join(' · ')}
        </span>
      )}
      {open && (
        <span className="day-city" aria-hidden="true">
          {t('calendar.unplanned')}
        </span>
      )}
      {arrivals.map((leg) => (
        // A way landing this day, having left the day before: its landing mark, when and where.
        <span
          key={`in-${leg.stop.id}`}
          className={`day-leg day-arrival mode-${leg.mode}`}
          title={t('calendar.arrives', { time: leg.time, place: leg.place })}
        >
          <ModeIcon mode="arrival" size={12} />
          <span className="day-leg-time">{leg.time}</span>
          <span className="day-leg-where">{leg.place}</span>
        </span>
      ))}
      {legs.map((leg) => (
        // Its mark, when it leaves, and where: a flight by its airports' codes, else the town.
        <span
          key={leg.stop.id}
          className={`day-leg mode-${leg.stop.out?.mode ?? 'travel'}`}
          title={how(leg.stop.out?.mode)}
        >
          <ModeIcon mode={leg.stop.out?.mode} size={12} />
          {leg.stop.out?.departureAt && (
            <span className="day-leg-time">
              {wallOf(leg.stop.out.departureAt, leg.stop.zone).time}
            </span>
          )}
          {whereTo(leg, onward) && (
            <span className="day-leg-where">{whereTo(leg, onward)}</span>
          )}
        </span>
      ))}
      {theirs.map(({ traveller, leg }) => (
        <span key={leg.id} className="day-theirs">
          {traveller.name.slice(0, 1)} · {leg.way}
        </span>
      ))}
      {tripDay.bookings.length > 0 && <span className="day-booked" aria-hidden="true" />}
      {/* Each person's mark by its icon and their initial; said in full when pointed at. */}
      {marks.length > 0 && (
        <span className="day-marks" aria-hidden="true">
          {marks.map(({ name, kind }) => (
            <span
              key={name}
              className={`day-mark mark-${kind}`}
              title={t('leave.mark', { name, kind: t(`leave.kind.${kind}`) })}
            >
              <DayKindIcon kind={kind} size={12} />
              {name.slice(0, 1)}
            </span>
          ))}
        </span>
      )}
      {kin.map((on) => (
        <KinCell key={on.trip.id} on={on} />
      ))}
    </button>
  );
}
